import { z } from "zod";
import { artifactTypeSchema, type ArtifactType } from "./artifacts";

export const publicValidationIssueSchema = z
  .object({
    code: z.string().max(80),
    path: z.array(z.union([z.string(), z.number()])).max(12),
  })
  .strict();

export const publicErrorSchema = z
  .object({
    code: z.string().min(1).max(80),
    message: z.string().min(1).max(240),
    request_id: z.uuid().nullable(),
    details: z.array(publicValidationIssueSchema).max(20).optional(),
  })
  .strict();

export type PublicError = z.infer<typeof publicErrorSchema>;

/**
 * Envelope que o Worker realmente devolve. `error` e `validationErrors` são
 * aliases legíveis mantidos enquanto o frontend legado existir como rollback;
 * `code`/`message`/`request_id`/`details` são a forma canônica desta fase.
 */
export const workerErrorResponseSchema = publicErrorSchema
  .extend({
    error: z.string().max(240).optional(),
    validationErrors: z.array(z.string().max(240)).max(20).optional(),
  })
  .strict();

export type WorkerErrorResponse = z.infer<typeof workerErrorResponseSchema>;

export const healthResponseSchema = z
  .object({ status: z.literal("ok"), marker: z.string().max(120) })
  .strict();
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const filtersResponseSchema = z
  .object({
    produtos: z.array(z.string()).max(500),
    categorias: z.array(z.string()).max(500),
    topicos: z.array(z.string()).max(500),
  })
  .strict();
export type FiltersResponse = z.infer<typeof filtersResponseSchema>;

/** Tipos MIME aceitos pelo Worker e limites por formato, em bytes decodificados. */
export const ATTACHMENT_LIMITS = Object.freeze({
  "image/jpeg": 3.75 * 1024 * 1024,
  "image/png": 3.75 * 1024 * 1024,
  "image/webp": 3.75 * 1024 * 1024,
  "application/pdf": 4 * 1024 * 1024,
});

export const attachmentMediaTypeSchema = z.enum([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);
export type AttachmentMediaType = z.infer<typeof attachmentMediaTypeSchema>;

/** O corpo carrega base64 puro; o prefixo `data:` é tolerado e removido no Worker. */
export const attachmentSchema = z
  .object({
    name: z.string().min(1).max(240).nullable().optional(),
    type: attachmentMediaTypeSchema,
    data: z.string().min(1).max(8_000_000),
  })
  .strict();
export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * Teto de anexos por pergunta: o Bedrock aceita até 5 documentos por mensagem
 * e o total decodificado precisa caber no corpo aceito pelo Worker.
 */
export const MAX_CHAT_ATTACHMENTS = 5;
export const CHAT_ATTACHMENTS_TOTAL_BYTES = 12 * 1024 * 1024;

export const chatHistoryTurnSchema = z.object({ q: z.string(), a: z.string() }).strict();

export const chatFiltersSchema = z
  .object({
    produto: z.string().max(200).optional(),
    categoria: z.string().max(200).optional(),
    topico: z.string().max(200).optional(),
    source_pdf: z.string().max(300).optional(),
  })
  .strict();
export type ChatFilters = z.infer<typeof chatFiltersSchema>;

export const chatRequestSchema = z
  .object({
    question: z.string().default(""),
    conversationId: z.uuid().nullable().optional(),
    examId: z.uuid().nullable().optional(),
    history: z.array(chatHistoryTurnSchema).default([]),
    filters: chatFiltersSchema.default({}),
    customInstructions: z.string().nullable().optional(),
    /** Formato legado de anexo único; clientes novos usam `attachments`. */
    attachment: attachmentSchema.nullable().optional(),
    attachments: z.array(attachmentSchema).max(MAX_CHAT_ATTACHMENTS).optional(),
  })
  .strict()
  .refine(
    (body) =>
      body.question.trim().length > 0 ||
      body.attachment != null ||
      (body.attachments?.length ?? 0) > 0,
    {
      message: "envie uma pergunta ou anexo",
      path: ["question"],
    },
  );
export type ChatRequest = z.infer<typeof chatRequestSchema>;

export const chatSourceSchema = z
  .object({
    source: z.string().max(500).nullable(),
    text: z.string(),
    score: z.number().nullable(),
  })
  .strict();
export type ChatSource = z.infer<typeof chatSourceSchema>;

export const contextMetricsSchema = z
  .object({
    chars: z.number().int().nonnegative(),
    estimatedTokens: z.number().int().nonnegative(),
    profileChars: z.number().int().nonnegative(),
    ragChars: z.number().int().nonnegative(),
    historyChars: z.number().int().nonnegative(),
    domains: z.array(z.string().max(40)).max(10),
    records: z.number().int().nonnegative(),
    periodDays: z.number().int().nullable(),
  })
  .strict();
export type ContextMetrics = z.infer<typeof contextMetricsSchema>;

export const chatUsageSchema = z
  .object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  })
  .strict();
export type ChatUsage = z.infer<typeof chatUsageSchema>;

export const chatDeltaSchema = z.object({ text: z.string() }).strict();

export const contextualActionStatusSchema = z.enum(["ready", "needs_information"]);
export type ContextualActionStatus = z.infer<typeof contextualActionStatusSchema>;

export const contextualActionSchema = z
  .object({
    type: artifactTypeSchema,
    status: contextualActionStatusSchema,
    recordCount: z.number().int().min(1).max(20).default(1),
    missingFields: z.array(z.string().trim().min(1).max(100)).max(12).default([]),
    /** Payloads ocultos produzidos junto da resposta; ausentes em mensagens legadas. */
    payloads: z.array(z.json()).min(1).max(20).optional(),
  })
  .strict()
  .superRefine((action, context) => {
    if (action.type !== "avaliacao_corporal" && action.recordCount !== 1) {
      context.addIssue({
        code: "custom",
        path: ["recordCount"],
        message: "somente avaliação corporal aceita múltiplos registros",
      });
    }
    if (action.status === "ready" && action.missingFields.length > 0) {
      context.addIssue({
        code: "custom",
        path: ["missingFields"],
        message: "uma ação pronta não pode ter lacunas",
      });
    }
    if (action.status === "needs_information" && action.missingFields.length === 0) {
      context.addIssue({
        code: "custom",
        path: ["missingFields"],
        message: "informe ao menos uma lacuna",
      });
    }
  });
export type ContextualAction = z.infer<typeof contextualActionSchema>;

export const contextualActionsSchema = z
  .array(contextualActionSchema)
  .max(5)
  .superRefine((actions, context) => {
    const seen = new Set<ArtifactType>();
    actions.forEach((action, index) => {
      if (seen.has(action.type)) {
        context.addIssue({
          code: "custom",
          path: [index, "type"],
          message: "ação contextual duplicada",
        });
      }
      seen.add(action.type);
    });
  });

export const chatActionsEventSchema = z
  .object({
    actions: contextualActionsSchema,
    answer: z.string().max(100_000),
  })
  .strict();

/** Nomes de evento SSE preservados do Worker JavaScript. */
export const CHAT_STREAM_EVENTS = Object.freeze([
  "sources",
  "context",
  "delta",
  "actions",
  "usage",
  "error",
  "done",
] as const);
export type ChatStreamEventName = (typeof CHAT_STREAM_EVENTS)[number];

export const generateArtifactRequestSchema = z
  .object({
    conversationId: z.uuid(),
    artifactType: artifactTypeSchema,
    sourceText: z.string().max(100_000).default(""),
    attachment: attachmentSchema.nullable().optional(),
    assetId: z.string().max(500).nullable().optional(),
    /** Quando presente, apenas valida e persiste; não chama o modelo novamente. */
    payload: z.json().optional(),
  })
  .strict()
  .refine((body) => body.sourceText.trim().length > 0 || body.attachment != null, {
    message: "conteudo de origem obrigatorio",
    path: ["sourceText"],
  });
export type GenerateArtifactRequest = z.infer<typeof generateArtifactRequestSchema>;

export const generateArtifactBatchRequestSchema = z
  .object({
    conversationId: z.uuid(),
    artifactType: z.literal("avaliacao_corporal"),
    sourceText: z.string().trim().min(1).max(100_000),
    expectedCount: z.number().int().min(2).max(20),
    /** Payloads já produzidos pelo chat; ausentes no fallback legado. */
    payloads: z.array(z.json()).min(2).max(20).optional(),
  })
  .strict();
export type GenerateArtifactBatchRequest = z.infer<typeof generateArtifactBatchRequestSchema>;

export const reviseArtifactRequestSchema = z.object({ payload: z.unknown() }).strict();

export const artifactDraftStatusSchema = z.enum(["pending", "approved", "rejected", "expired"]);
export type ArtifactDraftStatus = z.infer<typeof artifactDraftStatusSchema>;

/** Linha de `ai_drafts` devolvida pelo PostgREST e revalidada antes de sair do Worker. */
export const artifactDraftSchema = z
  .object({
    id: z.uuid(),
    user_id: z.uuid(),
    aluno_id: z.uuid(),
    conversation_id: z.uuid().nullable(),
    artifact_type: artifactTypeSchema,
    schema_version: z.literal(1),
    payload_json: z.unknown(),
    status: artifactDraftStatusSchema,
    source_kind: z.enum(["assistant_message", "exam_attachment", "template"]),
    source_template_id: z.uuid().nullable().optional(),
    model_id: z.string().max(200).nullable(),
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    approved_record_id: z.uuid().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    expires_at: z.string(),
    reviewed_at: z.string().nullable(),
  })
  .loose();
export type ArtifactDraft = z.infer<typeof artifactDraftSchema>;

export const artifactDraftResponseSchema = z
  .object({ draft: artifactDraftSchema, request_id: z.uuid().nullable().optional() })
  .strict();
export type ArtifactDraftResponse = z.infer<typeof artifactDraftResponseSchema>;

export const artifactDraftBatchResponseSchema = z
  .object({
    drafts: z.array(artifactDraftSchema).min(2).max(20),
    request_id: z.uuid().nullable().optional(),
  })
  .strict();
export type ArtifactDraftBatchResponse = z.infer<typeof artifactDraftBatchResponseSchema>;
